import React from "react";
import { useTranslation } from "react-i18next";
import { MdSchedule } from "react-icons/md";
import { LIVE_CHANNELS, SOON_COUNT } from "../salesChannels";

const StatTile = ({ children, label, highlight = false }) => (
  <div
    className={`min-w-[120px] rounded-[10px] border-[1px] border-ui-border px-[16px] py-[12px] ${
      highlight ? "bg-brand-light" : "bg-ui-surface"
    }`}
  >
    <div
      className={`flex items-center gap-1.5 text-2xl font-semibold leading-[1.1] ${
        highlight ? "text-brand" : "text-ui-text"
      }`}
    >
      {children}
    </div>
    <div
      className={`mt-0.5 text-xs ${highlight ? "text-brand" : "text-ui-text-muted"}`}
    >
      {label}
    </div>
  </div>
);

// Phone version of the tiles: one bordered strip, so three stats fit a row.
const StripStat = ({ children, label, highlight = false }) => (
  <div className="min-w-0 flex-1 px-[12px] py-2">
    <div
      className={`flex items-center gap-1.5 text-base font-semibold leading-tight ${
        highlight ? "text-brand" : "text-ui-text"
      }`}
    >
      {children}
    </div>
    <div
      className={`mt-0.5 text-[11px] ${highlight ? "text-brand" : "text-ui-text-muted"}`}
    >
      {label}
    </div>
  </div>
);

// syncingCount is null while the store list is unavailable; the stat is
// dropped rather than shown as a misleading 0.
const SalesChannelsHeader = ({ syncingCount }) => {
  const { t } = useTranslation("settings");
  const stats = [
    syncingCount != null && {
      label: t("marketplaces.header.syncing"),
      value: (
        <>
          <span className="h-2 w-2 rounded-full bg-state-success-dot" />
          {syncingCount}
        </>
      ),
    },
    { label: t("marketplaces.header.live"), value: LIVE_CHANNELS.length },
    {
      label: t("marketplaces.header.comingSoon"),
      highlight: true,
      value: (
        <>
          <MdSchedule className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          {SOON_COUNT}
        </>
      ),
    },
  ].filter(Boolean);

  return (
    <section className="border-b border-ui-border bg-ui-surface">
      <div className="mx-auto max-w-[1400px] px-[16px] pb-[16px] pt-[16px] sm:px-6 sm:pb-6 sm:pt-7">
        <div className="flex flex-wrap items-end justify-between gap-3.5 sm:gap-6">
          <div className="min-w-0 flex-[1_1_460px]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand">
              {t("marketplaces.header.eyebrow")}
            </div>
            <h1 className="m-[0px] mt-2 font-jakarta text-[20px] font-bold leading-[1.25] tracking-[-0.02em] text-ui-text sm:text-[28px]">
              {t("marketplaces.header.headline")}
            </h1>
            <p className="m-[0px] mt-2.5 hidden max-w-[640px] text-sm leading-[1.6] text-ui-text-muted sm:block">
              {t("marketplaces.header.intro")}
            </p>
          </div>
          <div className="flex w-full divide-x divide-ui-border rounded-[10px] border-[1px] border-ui-border bg-ui-surface sm:hidden">
            {stats.map(({ label, value, highlight }) => (
              <StripStat key={label} label={label} highlight={highlight}>
                {value}
              </StripStat>
            ))}
          </div>
          <div className="hidden flex-wrap gap-2.5 sm:flex">
            {stats.map(({ label, value, highlight }) => (
              <StatTile key={label} label={label} highlight={highlight}>
                {value}
              </StatTile>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SalesChannelsHeader;
